import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import Draggable from 'react-draggable';
import { Button } from 'react-bootstrap';
import withChartExport from '../../../components/ChartExporting';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

import { saveConfig, updateLineChart } from '../../../components/Filter/FilterActions';
import dateUtils from '../../../util/dates';
import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';

const config = {
  chart: {
    type: 'spline',
    backgroundColor: '#fff'
  },
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  tooltip: {
    shared: true,
    useHTML: true,
    formatter: function() {
      let tooltipHtml = '',
        s = [];
      this.points.forEach(point => {
        tooltipHtml += `<div class="tooltip-label-item""><span class="label-box" style="background: ${point.color}"></span>${point.series.name}%: ${point.point.y} day${point.point.y != 1 ? 's' : ''}</div>`;
      });
      s.push(tooltipHtml);
      return s;
    }
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    showFirstLabel: true,
    showLastTickLabel: true,
    title: {
      text: null
    },
    plotLines: [
      {
        value: 0
      }
    ]
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: false
    },
    title: {
      text: null
    },
    showFirstLabel: false,
    showLastTickLabel: false
  },
  plotOptions: {
    spline: {
      marker: {
        symbol: 'circle',
        enabledThreshold: 2,
        lineWidth: 1,
        radius: 5
      },
      states: {
        hover: {
          lineWidth: 2,
          halo: {
            opacity: 0
          },
          marker: {
            enabled: true
          }
        }
      }
    }
  },
  series: [
    {
      data: []
    }
  ]
};

const DEFAULT_POS = { x: 95, y: 85 };

export class PercentilesTrendChart extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isFullSreen: false,
      width: undefined,
      height: undefined,
      pos: DEFAULT_POS
    };
  }

  static defaultProps = {
    data: {}
  };

  componentDidMount() {
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    config.exporting = { ...config.exporting, ...this.props.chartExporting };

    this.chart = new Highcharts['Chart'](`avg_chart_container`, config);

    if (this.props.data && this.props.data.length) {
      this.updateDateFormat();
      this.calculateChartData();
    }
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.data, this.props.data)) {
      this.calculateChartData();
    }
    if (!_.isEqual(prevProps.percentiles, this.props.percentiles)) {
      this.calculateChartData();
    }
    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateDateFormat();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateDateFormat() {
    const format = dateUtils.createHighchartFormats(this.props.dateFormat);
    this.chart.update({
      xAxis: {
        dateTimeLabelFormats: {
          millisecond: format.month,
          second: format.month,
          minute: format.month,
          hour: format.month,
          day: format.month,
          week: format.month,
          month: format.month
        }
      }
    });
  }

  calculateChartData() {
    if (!this.props.data.length) {
      return false;
    }

    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    for (let i = this.props.percentiles.length - 1; i >= 0; i--) {
      const pObj = _.find(this.props.data, item => item.name == this.props.percentiles[i]);
      if (!pObj) {
        continue;
      }
      this.chart.addSeries(pObj, false);
    }

    this.chart.update({
      xAxis: {
        plotLines: [
          {
            color: '#F5F5F5',
            width: 1,
            value: this.props.data[0].data[0].x,
            label: {
              text: ''
            }
          }
        ]
      }
    });
    this.chart.redraw();
  }

  updateFontSize = () => {
    this.chart.update({
      exporting: {
        enabled: this.state.isFullSreen,
        chartOptions: {
          title: {
            text: 'Percentiles',
            style: {
              fontSize: '16px',
              fontWeight: 'bold'
            }
          },          
        }
      },

      legend: {
        enabled: this.state.isFullSreen,
        useHTML: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        labelFormatter: function () {
          return `
            <span class="color:#333333;font-size:12px;font-weight:normal">${this.name}%</span>
          `;
        }
      },
      
      yAxis: {
        gridLineWidth: +this.state.isFullSreen,
        labels: {
          enabled: this.state.isFullSreen
        }
      }
    });

    this.chart.reflow();
  };

  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullSreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          left: undefined,
          isFullSreen: false,
          pos: DEFAULT_POS
        },
        () => this.updateFontSize(false)
      );
    } else {
      this.setState(
        {
          width: '100%',
          height: '100%',
          left: -10,
          isFullSreen: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize(true)
      );
    }
  };

  render() {
    return (
      <Draggable position={this.state.pos} onStop={this.handleDrag} axis="both" bounds="parent">
        <div
          className={`box_white_shadow chart_averages_box long_title ${this.state.isFullSreen ? 'fullscreen' : ''}`}
          style={{
            width: this.state.width,
            height: this.state.height,
            left: this.state.left,
            position: this.state.isFullSreen ? 'absolute' : ''
          }}
        >
          <div className="box_white_title">
            <h3>Percentiles</h3>
            <div className='right-actions'>
              <Button variant='secondary' size='sm' className={`btn-action ${this.state.isFullSreen ? 'fullscreen' : ''}`} onClick={this.handleResize}>
                <img src={this.state.isFullSreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
              </Button>
              <Button className="btn-action" onClick={this.handleClose}>
                &times;
              </Button>
            </div>
          </div>
          <div id="avg_chart_container" />
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    close: () => dispatch(updateLineChart(false, 'ctsp')),
    saveConfig: () => dispatch(saveConfig())
  };
};

const mapStateToProps = state => {
  return {
    dateFormat: state.filters.dateFormat,
    percentiles: state.filters.ctsp.selectedPercentiles
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(PercentilesTrendChart));
